import mixpanel from "mixpanel-browser";
import { useEffect, useRef, useState } from "react";

import { usePreventRecordingInterruptions } from "./use-prevent-recording-interruptions";

// How often to poll /version.json when the tab is visible
const POLL_INTERVAL_MS = 300_000;

export function useVersionCheck(): { isUpdateAvailable: boolean } {
  const { shouldPreventInterruptions } =
    usePreventRecordingInterruptions();
  const hasShownVersionNotice = useRef(false);
  const [isUpdateAvailable, setIsUpdateAvailable] = useState(false);

  // VITE_APP_VERSION is a build-time constant injected by Vite — safe to capture
  // in the closure without adding it to the dependency array.

  useEffect(() => {
    const currentVersion = import.meta.env.VITE_APP_VERSION;
    if (!currentVersion) return;

    async function checkVersion() {
      if (document.visibilityState !== "visible") return;
      if (hasShownVersionNotice.current) return;

      try {
        const res = await fetch(`/version.json?_t=${Date.now()}`);
        if (!res.ok) return;
        const data = (await res.json()) as { version?: string };
        if (data.version && data.version !== currentVersion) {
          hasShownVersionNotice.current = true;
          setIsUpdateAvailable(true);
        }
      } catch {
        // Network error — silently ignore; next interval will retry
      }
    }

    // Check immediately and then on each interval tick
    checkVersion();
    const intervalId = setInterval(checkVersion, POLL_INTERVAL_MS);

    // Also check as soon as the user returns to a background tab so they
    // don't have to wait up to 5 mins for the next interval.
    document.addEventListener("visibilitychange", checkVersion);

    return () => {
      clearInterval(intervalId);
      document.removeEventListener("visibilitychange", checkVersion);
    };
  }, []);

  // Handle chunk load 404s that occur after a deploy. If the user is not
  // recording, hard-reload to fetch the latest chunks. If they are recording,
  // show a banner so they can reload when it's safe to do so.
  useEffect(() => {
    function handlePreloadError() {
      mixpanel.track("Chunk Load Error", {
        is_recording: shouldPreventInterruptions,
      });
      if (shouldPreventInterruptions) {
        setIsUpdateAvailable(true);
      } else {
        window.location.reload();
      }
    }

    window.addEventListener("vite:preloadError", handlePreloadError);
    return () =>
      window.removeEventListener(
        "vite:preloadError",
        handlePreloadError,
      );
  }, [shouldPreventInterruptions]);

  return { isUpdateAvailable };
}
